import React from "react";
import { Icon, Spin } from "antd";

const USAGE_ALERT_THRESHOLD = 0.9;

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatDays = (value) => {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return String(number);
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

const isHighCreditUsage = (used, limit) => {
  const usedAmount = toNumber(used);
  const limitAmount = toNumber(limit);
  if (usedAmount == null || limitAmount == null || limitAmount <= 0) {
    return false;
  }
  return usedAmount / limitAmount >= USAGE_ALERT_THRESHOLD;
};

const isHighDaysUsage = (usedDays, creditDays) => {
  const used = toNumber(usedDays);
  const totalDays = toNumber(creditDays);
  if (used == null || totalDays == null || totalDays <= 0) {
    return false;
  }
  return used / totalDays >= USAGE_ALERT_THRESHOLD;
};

const getLimitLeft = (limit, used) => {
  const limitAmount = toNumber(limit);
  const usedAmount = toNumber(used);
  if (limitAmount == null || usedAmount == null) return null;
  return limitAmount - usedAmount;
};

// pendingdays from API = days already used
const getDaysLeft = (creditDays, usedDays) => {
  const totalDays = toNumber(creditDays);
  const used = toNumber(usedDays);
  if (totalDays == null || used == null) return null;
  return totalDays - used;
};

const Metric = ({ label, value, alert }) => (
  <div className={`credit-limit-metric${alert ? " is-alert" : ""}`}>
    <span className="credit-limit-metric-label">{label}</span>
    <span className="credit-limit-metric-value">{value}</span>
  </div>
);

const CreditLimitCard = ({ title, data, loading, error, showTermsNote }) => {
  if (loading) {
    return (
      <div className="credit-limit-card credit-limit-card-loading">
        <Spin size="small" />
        <span>Checking credit…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="credit-limit-card credit-limit-card-error">
        <Icon type="warning" />
        <span>{error}</span>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const limit = data.customer_credit_limit;
  const used = data.credit_limit_used;
  const days = data.customer_credit_days;
  const daysUsed = data.pendingdays; // API pendingdays = used days

  const isHighUsage = isHighCreditUsage(used, limit);
  const isHighDays = isHighDaysUsage(daysUsed, days);
  const isAlert = isHighUsage || isHighDays;

  const limitLeft = getLimitLeft(limit, used);
  const daysLeft = getDaysLeft(days, daysUsed);

  return (
    <div
      className={`credit-limit-card ${
        isAlert ? "credit-limit-card-breach" : "credit-limit-card-ok"
      }`}
    >
      <div className="credit-limit-card-main">
        <div className="credit-limit-card-identity">
          <Icon
            type={isAlert ? "exclamation-circle" : "safety-certificate"}
          />
          <span className="credit-limit-card-title">{title}</span>
          {isHighUsage ? (
            <span className="credit-limit-status is-breach">90%+ Used</span>
          ) : null}
          {isHighDays ? (
            <span className="credit-limit-status is-breach">90%+ Days</span>
          ) : null}
          {showTermsNote ? (
            <span className="credit-limit-tnc">* T&amp;C apply</span>
          ) : null}
        </div>

        <div className="credit-limit-metrics">
          <Metric label="Limit" value={formatAmount(limit)} />
          <Metric label="Used" value={formatAmount(used)} alert={isHighUsage} />
          <Metric
            label="Remaining Limit"
            value={formatAmount(limitLeft)}
            alert={isHighUsage}
          />
          <Metric label="Days" value={formatDays(days)} alert={isHighDays} />
          <Metric
            label="Days used"
            value={formatDays(daysUsed)}
            alert={isHighDays}
          />
          <Metric
            label="Remaining Days"
            value={formatDays(daysLeft)}
            alert={isHighDays}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditLimitCard;
